import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API } from '../../utils/apiConfig';
import { EmptyState, ExportBtn, Kpi, PeriodNote, SearchBox, Section } from './PosBits';
import { PosTable } from './PosTable';
import { errMsg, monthLabel, num, pctf, usePaged } from './posUtils';

const SettingsCard = ({ current, isAdmin, onSaved }) => {
  const [v, setV] = useState(current);
  useEffect(() => setV(current), [current]);
  const save = async () => {
    try {
      await axios.put(`${API}/pos/settings`, { lost_threshold_months: Number(v) });
      toast.success('Settings saved');
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title="Settings" testid="pos-settings-card">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xs text-slate-500 mb-1">Mark a customer “Lost” after this many months without a purchase</div>
          <Input type="number" min={1} max={24} value={v ?? ''} onChange={(e) => setV(e.target.value)} disabled={!isAdmin} className="w-28 h-9" data-testid="settings-lost-threshold" />
        </div>
        {isAdmin && <Button size="sm" onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-btn">Save</Button>}
        {!isAdmin && <span className="text-xs text-slate-400">Only admins can change this.</span>}
      </div>
    </Section>
  );
};

const LEDGER = [
  { key: 'customer_id', label: 'Customer ID' },
  { key: 'name', label: 'Name', render: (v) => <b>{v}</b> },
  { key: 'mobile', label: 'Mobile', sortable: false },
  { key: 'month', label: 'Month', render: monthLabel },
  { key: 'points_earned', label: 'Earned', align: 'right', render: num },
  { key: 'points_burned', label: 'Burned', align: 'right', render: num },
  { key: 'net_points', label: 'Net', align: 'right', render: num },
  { key: 'running_net', label: 'Running Net', align: 'right', render: num },
];

export const PointsTab = ({ params, isAdmin, onSettingsSaved, openCustomer }) => {
  const t = usePaged('points', params, 'month');
  const d = t.data;
  if (d?.empty) return <EmptyState />;
  const tot = d?.totals;
  const mw = (d?.monthwise || []).filter((r) => r.has_data).map((r) => ({ ...r, label: monthLabel(r.month) }));
  return (
    <div className="space-y-4" data-testid="points-tab">
      {tot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Points Earned" value={num(tot.points_earned)} testid="points-kpi-earned" />
          <Kpi label="Points Burned" value={num(tot.points_burned)} tone="text-amber-700" testid="points-kpi-burned" />
          <Kpi label="Net Points" value={num(tot.net_points)} tone="text-emerald-700" sub="Earned − Burned" testid="points-kpi-net" />
          <Kpi label="Burn Rate" value={pctf(tot.burn_rate)} sub="Burned ÷ Earned" testid="points-kpi-burnrate" />
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Monthwise Earned vs Burned" testid="points-chart">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={mw}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
              <Bar dataKey="points_earned" name="Earned" fill="#059669" /><Bar dataKey="points_burned" name="Burned" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </Section>
        <div className="space-y-4">
          <SettingsCard current={d?.settings?.lost_threshold_months} isAdmin={isAdmin} onSaved={() => { t.reload(); onSettingsSaved(); }} />
          <Section title="Points by Venue / Booking Source" testid="points-by-venue">
            {d?.by_venue?.available || d?.by_source?.available ? (
              <PosTable testid="points-venue-table" rows={[...(d.by_venue.items || []), ...(d.by_source.items || [])]} columns={[
                { key: 'name', label: 'Venue / Source' }, { key: 'points_earned', label: 'Earned', align: 'right', render: num },
                { key: 'points_burned', label: 'Burned', align: 'right', render: num }]} />
            ) : <p className="text-sm text-slate-400">Not available — the imported POS files do not include venue or booking source.</p>}
          </Section>
        </div>
      </div>
      <Section title="Customer Points Ledger (monthly)" right={<div className="flex gap-2"><SearchBox value={t.search} onChange={t.setSearch} testid="points-search" /><ExportBtn section="points" params={params} /></div>}>
        <div className="mb-2"><PeriodNote data={d} /></div>
        <PosTable columns={LEDGER} rows={d?.items} sort={t.sort} order={t.order} onSort={t.onSort} page={t.page} total={d?.total}
          onPage={t.setPage} loading={t.loading} testid="points-ledger-table" onRowClick={(r) => openCustomer(r.customer_id)} />
      </Section>
    </div>
  );
};
